/**
 * The install snippet — ONE definition, because two of them drifted and the drift shipped.
 *
 * 🔴 WHAT HAPPENED: the onboarding wizard told partners to load `${origin}/chat-widget.js` while
 * the real asset is at `/widget/chat-widget.js`. One missing path segment, and nothing anywhere
 * reported an error: the SPA catch-all answers 200 for every unknown path, so the browser received
 * `text/html` (the 462-byte app shell), executed it as JavaScript, and the widget silently never
 * appeared. Measured on prod — `/chat-widget.js` was byte-identical to `/` and to a nonsense
 * control path, all sha 2088d5eb…, while `/widget/chat-widget.js` returned 16920 bytes of
 * `application/javascript`. Pasting that snippet is THE one action a partner has to take, and it
 * could not work.
 *
 * The console page had it right and the wizard had it wrong, which is the whole argument for this
 * file: the fix is not "correct the string", it is "make there be one string". A constant cannot
 * disagree with itself.
 *
 * WIDGET_PATH is asserted against the built asset's real location by
 * scripts/test/widget-snippet.ts, so a future move of the widget output reddens the gate instead
 * of silently reintroducing this.
 */

/** Where the built widget actually lands. Mirrors package.json's build: dist/widget/chat-widget.js. */
export const WIDGET_PATH = '/widget/chat-widget.js';

/** The exact line a partner pastes before </body>. */
export function widgetSnippet(origin: string, accountId: string | null | undefined): string {
  return `<script src="${origin}${WIDGET_PATH}" data-account-id="${accountId || ''}"></script>`;
}

/**
 * A pre-filled mailto: for "email these steps to your developer".
 *
 * No recipient: the user's own mail client asks for it. That is the point — we never handle an
 * arbitrary address, so there is no To:-header injection surface and nothing to sanitise, unlike
 * a server-side send of the same feature.
 *
 * encodeURIComponent on the body is load-bearing rather than cosmetic: the body CONTAINS a
 * snippet full of `&`, `=`, `<` and `"`, and a bare `&` would truncate the body at the first
 * ampersand and silently mail a half-snippet — a broken paste being worse than no email.
 */
export function mailtoInstallSteps(
  origin: string,
  accountId: string | null | undefined,
  snippet = widgetSnippet(origin, accountId),
): string {
  const subject = 'Install the Biombo chat widget on our site';
  const body = [
    'Hi — please add this one line to our website, just before the closing </body> tag:',
    '',
    snippet,
    '',
    'That is the whole installation. Nothing else needs to change, and the widget appears as soon',
    'as the page is published.',
    '',
    `Dashboard: ${origin}`,
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
